import { captureFrameDomPng } from './captureFrameDom';

/**
 * Rasterize a frame to a base64 PNG for agent vision (export_frame /
 * screenshot / cover). Two paths, best-fidelity first:
 *
 * - **Unified canvas crop:** the single top-level `<canvas layoutsubtree>`
 *   paints every frame with effects baked into the pixels. We read the frame
 *   root's on-screen rect and crop the matching region out of that bitmap —
 *   cheapest + highest fidelity when a painted canvas exists (Canary).
 * - **DOM path (universal fallback):** no painted canvas (DOM fallback /
 *   non-Canary, or a transient toBlob failure) → SVG foreignObject snapshot of
 *   the live DOM (`captureFrameDom.ts`). Works in every browser, CSS-filter
 *   fidelity. This is what keeps agent vision working for ~all production users.
 */
export async function captureFramePng(frameId: string): Promise<{ base64: string; w: number; h: number }> {
  const root = document.querySelector(`[data-frame-id="${CSS.escape(frameId)}"]`);
  if (!root) throw new Error(`frame "${frameId}" is not on the canvas — focus_view it first`);

  const canvas = findUnifiedCanvas(root);
  if (canvas && canvas.width > 0 && canvas.height > 0) {
    // Map the frame root's client rect into canvas-bitmap pixel space. The
    // unified canvas has no DPR scaling (WICG rule) and fills the viewport at
    // CSS size, so bitmap px ≈ CSS px offset from the canvas's top-left.
    const fr = (root as HTMLElement).getBoundingClientRect();
    const cr = canvas.getBoundingClientRect();
    const sx = Math.max(0, Math.round((fr.left - cr.left) * (canvas.width / cr.width)));
    const sy = Math.max(0, Math.round((fr.top - cr.top) * (canvas.height / cr.height)));
    const sw = Math.max(1, Math.min(canvas.width - sx, Math.round(fr.width * (canvas.width / cr.width))));
    const sh = Math.max(1, Math.min(canvas.height - sy, Math.round(fr.height * (canvas.height / cr.height))));

    const out = document.createElement('canvas');
    out.width = sw;
    out.height = sh;
    const ctx = out.getContext('2d');
    if (ctx) {
      ctx.drawImage(canvas, sx, sy, sw, sh, 0, 0, sw, sh);
      const blob = await new Promise<Blob | null>((res) => out.toBlob(res, 'image/png'));
      if (blob) {
        const dataUrl = await new Promise<string>((res, rej) => {
          const reader = new FileReader();
          reader.onload = () => res(reader.result as string);
          reader.onerror = () => rej(new Error('FileReader failed'));
          reader.readAsDataURL(blob);
        });
        return { base64: dataUrl.split(',')[1] ?? '', w: sw, h: sh };
      }
    }
    // ctx/toBlob can fail transiently — fall through to the DOM path.
  }
  return captureFrameDomPng(frameId);
}

/** The unified layoutsubtree canvas. Walks up from the frame root to its canvas
 *  wrapper, else falls back to the largest painted canvas on the page. Null when
 *  nothing is painted (DOM fallback) → caller uses the DOM rasterizer. */
function findUnifiedCanvas(root: Element): HTMLCanvasElement | null {
  const ancestorCanvas = root.closest('div')?.parentElement?.querySelector?.('canvas');
  if (ancestorCanvas instanceof HTMLCanvasElement && ancestorCanvas.width) return ancestorCanvas;
  const all = Array.from(document.querySelectorAll('canvas')) as HTMLCanvasElement[];
  return all
    .filter((c) => c.width > 0 && c.height > 0)
    .sort((a, b) => b.width * b.height - a.width * a.height)[0] ?? null;
}
